import { EditOutlined, UserOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Descriptions,
  Divider,
  Input,
  Modal,
  Skeleton,
  Tag,
} from "antd";
import React, { useEffect, useState } from "react";
import { $t, i18n } from "~/i18n.js";
import { api } from "~/lib/api.js";
import { _parseDateTime, parseMinutesLong } from "../../lib/localize.js";
import { colors } from "../../theme/colors.js";
import { TagIcon } from "../atom/TagIcon.jsx";
import { SluitButton } from "../buttons/SluitButton.jsx";
import { FlightMotorTime } from "../form/FlightMotorTime.jsx";
import { LandingIcon, StartIcon } from "../icon/icon.jsx";
import { IGCButton } from "../IGC/IGC.button.jsx";
import { VoucherSelector } from "../voucher/VoucherSelector.jsx";
import { FlightDetails } from "./sections/flightDetails.jsx";
import { FlightTags } from "./sections/FlightTags.jsx";
import { OGNtimeIcon } from "./sections/Ogn.jsx";
import { PilotLink } from "./sections/PilotLink.jsx";
import { SignFlightCollapse } from "./sections/signFlight.jsx";

const descriptionRow = (value, label, show, postfix, renderAsAlert) =>
  show && (
    <Descriptions.Item label={label}>
      {renderAsAlert ? (
        <>
          <Tag icon={<UserOutlined />} color="blue">
            {value} {postfix}
          </Tag>
        </>
      ) : (
        <>
          {value} {postfix}
        </>
      )}
    </Descriptions.Item>
  );

export const FlightViewModal = ({
  onClose,
  flight_uuid = null,
  flight_object = null,
  currentUser,
  updateFlight,
  onUpdateFlight,
  showPilotLink = false,
  hideSigning = false,
}) => {
  // State for fetched flights
  const [flight, setFlight] = useState(null);
  const [sleepFlight, setSleepFlight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFlight, setShow] = useState(false);

  // Helper to assign which is sleep and which is main
  const assignFlights = (flightA, flightB) => {
    const isSleep = (f) => f && ["tmg-a", "sep-a"].includes(f.start_methode);
    if (isSleep(flightA)) {
      setSleepFlight(flightA);
      setFlight(flightB);
    } else if (isSleep(flightB)) {
      setSleepFlight(flightB);
      setFlight(flightA);
    } else {
      setFlight(flightA);
      setSleepFlight(flightB);
    }
    setShow(true);
  };

  // Fetch logic if not provided
  useEffect(() => {
    let cancelled = false;
    const fetchFlights = async () => {
      setLoading(true);

      // If flight_object is provided
      if (flight_object) {
        if (!flight_object.sleep_uuid) {
          setFlight(flight_object);
          setSleepFlight(null);
          setShow(true);
        } else {
          const sleepRes = await api.get(
            `flights/${flight_object.sleep_uuid}/view.json`,
          );
          if (!cancelled) {
            if (sleepRes) assignFlights(flight_object, sleepRes);
            else {
              setFlight(flight_object);
              setSleepFlight(null);
              setShow(true);
            }
          }
        }
        setLoading(false);
        return;
      }

      // If flight_uuid is provided
      if (flight_uuid) {
        const res = await api.get(`flights/${flight_uuid}/view.json`);
        if (!cancelled && res) {
          if (!res.sleep_uuid) {
            setFlight(res);
            setSleepFlight(null);
            setShow(true);
          } else {
            const sleepRes = await api.get(
              `flights/${res.sleep_uuid}/view.json`,
            );
            if (sleepRes) assignFlights(res, sleepRes);
            else {
              setFlight(res);
              setSleepFlight(null);
              setShow(true);
            }
          }
        }
        setLoading(false);
      }
    };

    if (!flight_uuid && !flight_object) {
      setFlight(null);
      setSleepFlight(null);
      setLoading(false);
      setShow(false);
      cancelled = true;
      return;
    }
    fetchFlights();
    return () => {
      cancelled = true;
    };
  }, [flight_uuid, flight_object]);

  const [signed, setSigned] = useState(null);
  const [notitie, setNotitie] = useState("");
  const [bijzonderheden, setBijzonderheden] = useState("");
  useEffect(() => {
    setNotitie(flight ? flight?.notitie : "");
    setBijzonderheden(flight ? flight?.bijzonderheden : "");
    setSigned(flight?.signed);
  }, [flight]);

  const saveNotitie = async () => {
    setLoading(true);
    await updateFlight({ uuid: flight.uuid, notitie, bijzonderheden });
    onUpdateFlight({ flight });
    setLoading(false);
    onClose();
  };

  if (!showFlight) return null;

  const flightDatum = isNaN(flight?.datum)
    ? `"${flight?.datum}"`
    : flight?.datum;

  if (loading && (flight_object || flight_uuid)) {
    return (
      <Modal
        width={550}
        open={true}
        onCancel={onClose}
        footer={null}
        className="scrollable"
      >
        <Skeleton active paragraph={{ rows: 8 }} />
      </Modal>
    );
  }

  if (flight) {
    return (
      <Modal
        width={550}
        title={`${$t("Vlucht")} ${flightDatum} ${
          flight.volg_nummer ? " #" + flight.volg_nummer : ""
        }`}
        open={!!flight}
        onCancel={onClose}
        className="scrollable"
        footer={[
          <Button
            type="primary"
            key="edit"
            disabled={
              flight.is_prive
                ? !(
                    flight.gezagvoerder_id === currentUser?.id ||
                    flight.tweede_inzittende_id === currentUser?.id
                  )
                : !flight.dag_id
            }
            href={
              flight.is_prive
                ? `/my_starts/${flight.uuid}`
                : `/start/${flight.dag_id}/${flight.uuid}`
            }
            icon={<EditOutlined />}
          >
            {$t("Wijzig")}
          </Button>,
          <SluitButton key="sluit" onClick={onClose} />,
        ]}
      >
        {flight._errors?.map((error, key) => (
          <Alert key={key} message={error} type="error" showIcon />
        ))}

        {/* Kist & Sleepkist */}
        <Descriptions size="small" column={1} layout="horizontal">
          {descriptionRow(
            <>
              {`${flight.callsign || ""} ${flight.registratie || ""} (${
                flight.type || ""
              })`}
              <Tag style={{ marginLeft: 8 }} color={colors.primary_light}>
                {$t(flight.start_methode)}
              </Tag>
            </>,
            $t("Kist"),
            true,
          )}
          {sleepFlight &&
            descriptionRow(
              <>
                {`${sleepFlight.callsign || ""} ${
                  sleepFlight.registratie || ""
                } (${sleepFlight.type || ""})`}
                <Tag style={{ marginLeft: 8 }} color={colors.primary_light}>
                  {$t(sleepFlight.start_methode)}
                </Tag>
              </>,
              $t("Sleepkist"),
              true,
            )}
        </Descriptions>

        {/* Vliegers */}
        <Divider orientation="left" plain>
          {$t("Vliegers")}
        </Divider>
        <Descriptions size="small" column={1} layout="horizontal">
          {descriptionRow(
            showPilotLink ? (
              <PilotLink
                name={flight.gezagvoerder_naam}
                id={flight.gezagvoerder_id}
                object={flight.gezagvoerder}
                betalend_lid_id={flight.betalend_lid_id}
              />
            ) : (
              flight.gezagvoerder_naam
            ),
            $t("Gezagvoerder"),
            true,
          )}
          {descriptionRow(
            showPilotLink ? (
              <PilotLink
                name={flight.tweede_inzittende_naam}
                id={flight.tweede_inzittende_id}
                object={flight.tweede_inzittende}
                betalend_lid_id={flight.betalend_lid_id}
              />
            ) : (
              flight.tweede_inzittende_naam
            ),
            $t("Tweede-inzittende"),
            true,
          )}
          {sleepFlight &&
            descriptionRow(
              sleepFlight.gezagvoerder_naam,
              $t("Sleepvlieger"),
              true,
            )}
          {sleepFlight &&
            sleepFlight.tweede_inzittende_naam !== "" &&
            descriptionRow(
              sleepFlight.tweede_inzittende_naam,
              $t("2e Sleepvlieger"),
              true,
            )}
          {flight.betalend_lid &&
            descriptionRow(flight.betalend_lid.name, $t("Betalend lid"), true)}

          {flight.voucher &&
            descriptionRow(
              <VoucherSelector
                size="small"
                flight={flight}
                sleepFlight={sleepFlight}
                readOnly={true}
              />,
              $t("Bon"),
              true,
            )}
        </Descriptions>

        {/* Tijden */}
        <Divider orientation="left" plain>
          {$t("Tijden")}
        </Divider>
        <Descriptions size="small" column={1} layout="horizontal">
          {descriptionRow(
            <span>
              <span style={{ marginRight: 8 }}>
                <OGNtimeIcon
                  ogn_indicator={flight.start_ogn}
                  defaultIcon={<StartIcon color={colors.primary_dark} />}
                />{" "}
                {flight.start_tijd}
              </span>
              <span style={{ marginRight: 8 }}>
                <OGNtimeIcon
                  ogn_indicator={flight.landings_ogn}
                  defaultIcon={
                    <LandingIcon color={colors.primary_dark} size={16} />
                  }
                />{" "}
                {flight.landings_tijd}
              </span>
            </span>,
            $t("Tijden"),
            true,
          )}
          {descriptionRow(
            <span style={{ color: "#888" }}>
              {parseMinutesLong(flight.vluchtduur)}
            </span>,
            $t("Vluchtduur"),
            true,
          )}
          {flight.blocktime > 0 &&
            descriptionRow(
              <span style={{ color: "#888" }}>
                {parseMinutesLong(flight.blocktime)}
              </span>,
              $t("Bloktijd"),
              true,
            )}

          {flight.motor_minutes > 0 &&
            descriptionRow(
              <span style={{ color: "#888" }}>
                <FlightMotorTime motorMinutes={flight.motor_minutes} />
              </span>,
              $t("Motor tijd"),
              true,
            )}

          {sleepFlight &&
            descriptionRow(
              <span>
                <span style={{ marginRight: 8 }}>
                  <StartIcon color={colors.primary_dark} />
                  <OGNtimeIcon ogn_indicator={sleepFlight.start_ogn} />{" "}
                  {sleepFlight.start_tijd}
                </span>
                <span style={{ marginRight: 8 }}>
                  <span style={{ marginLeft: 8, marginRight: 2 }}>
                    <LandingIcon color={colors.primary_dark} size={16} />
                  </span>
                  {sleepFlight.landings_tijd}
                </span>
              </span>,
              $t("Sleep tijden"),
              true,
            )}
          {sleepFlight &&
            descriptionRow(
              <span style={{ color: "#888" }}>
                {parseMinutesLong(sleepFlight.vluchtduur)}
              </span>,
              $t("Sleep vluchtduur"),
              true,
            )}
          {sleepFlight &&
            sleepFlight.blocktime > 0 &&
            descriptionRow(
              <span style={{ color: "#888" }}>
                {parseMinutesLong(sleepFlight.blocktime)}
              </span>,
              $t("Sleep bloktijd"),
              true,
            )}
        </Descriptions>

        {/* Details */}
        <Divider orientation="left" plain>
          {$t("Details")}
        </Divider>
        <Descriptions size="small" column={1} layout="horizontal">
          {descriptionRow(
            <FlightTags flight={flight} pilot={currentUser} hidePrive hideTags />,
            "",
            true,
          )}

          {flight.height != null &&
            descriptionRow(
              flight.height > 0
                ? i18n.parseLocaleHeightString(flight.height)
                : "-",
              $t("Hoogte"),
              true,
            )}

          {descriptionRow(
            <span>
              <TagIcon
                color="default"
                icon={<StartIcon color={colors.primary_dark} />}
                style={{ marginRight: 8 }}
              >
                {flight.vertrek_vliegveld}
              </TagIcon>
              <TagIcon
                color={
                  flight.vertrek_vliegveld !== flight.aankomst_vliegveld
                    ? "green"
                    : "default"
                }
                icon={<LandingIcon color={colors.primary_dark} size={16} />}
              >
                {flight.aankomst_vliegveld}
              </TagIcon>
            </span>,
            "",
            true,
          )}
          {!flight.is_prive && (
            <Descriptions.Item label="IGC">
              <IGCButton
                flight={flight}
                title={$t("Open kaart")}
                onUpdateFlight={onUpdateFlight}
                skipInfo={false}
              />
            </Descriptions.Item>
          )}

          {!updateFlight &&
            descriptionRow(
              flight.bijzonderheden || $t("Geen"),
              $t("Aantekeningen"),
              true,
            )}
          {!updateFlight &&
            flight.is_prive &&
            flight.notitie &&
            descriptionRow(flight.notitie, $t("Privé notitie"), true)}

          {flight.is_prive && (
            <>
              {descriptionRow(
                _parseDateTime(flight.date_created),
                $t("Aangemaakt"),
                !!flight.date_created,
              )}

              {descriptionRow(
                _parseDateTime(flight.date_updated),
                $t("Laatst gewijzigd"),
                !!flight.date_updated,
              )}
            </>
          )}
        </Descriptions>

        {!flight.is_prive && <FlightDetails flight={flight} />}

        {sleepFlight && (
          <section>
            {/* Sleep details */}
            <Divider orientation="left" plain>
              {$t("Sleep details")}
            </Divider>
            <Descriptions size="small" column={1} layout="horizontal">
              {descriptionRow(
                <FlightTags
                  flight={sleepFlight}
                  pilot={currentUser}
                  hidePrive
                />,
                "",
                true,
              )}

              {sleepFlight.height != null &&
                descriptionRow(
                  sleepFlight.height > 0
                    ? i18n.parseLocaleHeightString(sleepFlight.height)
                    : "-",
                  $t("Sleephoogte"),
                  true,
                )}

              {descriptionRow(
                <span>
                  <TagIcon
                    color="default"
                    icon={<StartIcon color={colors.primary_dark} />}
                    style={{ marginRight: 8 }}
                  >
                    {sleepFlight.vertrek_vliegveld}
                  </TagIcon>
                  <TagIcon
                    color={
                      sleepFlight.vertrek_vliegveld !==
                      sleepFlight.aankomst_vliegveld
                        ? "green"
                        : "default"
                    }
                    icon={<LandingIcon color={colors.primary_dark} size={16} />}
                  >
                    {sleepFlight.aankomst_vliegveld}
                  </TagIcon>
                </span>,
                "",
                true,
              )}

              {!sleepFlight.is_prive && (
                <Descriptions.Item label="IGC">
                  <IGCButton
                    flight={sleepFlight}
                    title={$t("Open kaart")}
                    onUpdateFlight={onUpdateFlight}
                    skipInfo={false}
                  />
                </Descriptions.Item>
              )}
            </Descriptions>
            {!sleepFlight.is_prive && (
              <FlightDetails
                title={$t("Sleep wijzigingen")}
                flight={sleepFlight}
              />
            )}
          </section>
        )}

        {(!hideSigning || updateFlight) && (
          <>
            <Divider orientation="left" plain>
              {$t("Notitie")}
            </Divider>

            {!hideSigning && (
              <div style={{ marginBottom: 12 }}>
                <SignFlightCollapse
                  flight={{ ...flight, signed: signed || flight.signed }}
                  updateFlight={({ flight }) => {
                    setSigned(flight.signed);
                    onUpdateFlight && onUpdateFlight({ flight });
                    onClose();
                  }}
                />
              </div>
            )}

            {updateFlight && (
              <>
                <p>{$t("Beschrijving (zichtbaar voor iedereen)")}:</p>
                <Input.TextArea
                  rows={2}
                  value={bijzonderheden}
                  onChange={(elm) =>
                    setBijzonderheden(elm.target.value.substr(0, 500))
                  }
                />
                <p>{$t("Eigen notitie (alleen zichtbaar voor vliegers)")}:</p>
                <Input.TextArea
                  rows={2}
                  value={notitie}
                  onChange={(elm) =>
                    setNotitie(elm.target.value.substr(0, 500))
                  }
                />
                <Button
                  style={{ marginTop: 6 }}
                  onClick={() => saveNotitie()}
                  type="primary"
                  loading={loading}
                  disabled={
                    notitie === flight.notitie &&
                    bijzonderheden === flight.bijzonderheden
                  }
                >
                  {$t("Sla op")}
                </Button>
              </>
            )}
          </>
        )}
        {(flight?.uuid || sleepFlight?.uuid) && (
          <p className="gray small" style={{ marginTop: 12 }}>
            {flight?.uuid && <span>Uuid: {flight?.uuid}</span>}
            {sleepFlight?.uuid && (
              <span style={{ marginLeft: 8 }}>
                {$t("Sleep")} Uuid: {sleepFlight?.uuid}
              </span>
            )}
          </p>
        )}
      </Modal>
    );
  }
  return null;
};
